import { Link } from "react-router-dom";
import { MapPin, Clock, Phone, Mail, Facebook, Instagram, Youtube, Users, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

export default function LocationAndFooter() {
  const LOGO = "https://assets.wordaliveglobal.org/branding/wagc_logo_small.png";

  return (
    <section data-testid="location-footer-section" className="bg-navy text-white relative overflow-hidden flex flex-col">
      {/* Top Location Teaser Section */}
      <div className="py-20 md:py-28 relative z-10 container-page flex-1">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-6 border border-gold/20 shadow-inner">
            <MapPin className="h-8 w-8 text-gold" />
          </div>
          
          <h2 className="font-heading text-4xl md:text-5xl text-bone mb-6">
            Visit Us at the Center
          </h2>
          
          <div className="w-12 h-1 bg-gold rounded-full mx-auto mb-10 opacity-80" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mb-12 text-center">
            {/* Address */}
            <div className="flex flex-col items-center p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-gold/30 transition-colors duration-300">
              <MapPin className="h-6 w-6 text-gold mb-4" />
              <h3 className="font-heading text-xl text-bone mb-2">Location</h3>
              <p className="text-white/70 text-sm leading-relaxed">
                1120 W Panola Street<br />
                Carthage, TX 75633
              </p>
            </div>
            
            {/* Times */}
            <div className="flex flex-col items-center p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-gold/30 transition-colors duration-300">
              <Clock className="h-6 w-6 text-gold mb-4" />
              <h3 className="font-heading text-xl text-bone mb-2">Gatherings</h3>
              <p className="text-white/70 text-sm leading-relaxed">
                Sunday Service<br />
                10:30 a.m.
              </p>
            </div>

            {/* Contact */}
            <div className="flex flex-col items-center p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-gold/30 transition-colors duration-300">
              <Phone className="h-6 w-6 text-gold mb-4" />
              <h3 className="font-heading text-xl text-bone mb-2">Contact</h3>
              <p className="text-white/70 text-sm leading-relaxed">
                (903) 690-3433<br />
                contactus@wordalivetx.com
              </p>
            </div>
          </div>

          <a 
            href="https://www.google.com/maps/search/?api=1&query=1120+W+Panola+Street,+Carthage,+TX+75633" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Button className="bg-gold text-navy hover:bg-gold/90 shadow-[0_0_15px_rgba(212,175,55,0.2)] rounded-full px-10 h-12 uppercase tracking-wider font-bold text-xs transition-all duration-300 hover:-translate-y-0.5">
              Get Directions <ExternalLink className="h-4 w-4 ml-2" />
            </Button>
          </a>
        </div>
      </div>

      {/* Unified Footer Section */}
      <footer className="bg-[#050e1a] border-t border-white/10 pt-16 pb-8 px-4 sm:px-6 relative z-10">
        <div className="container-page max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8 mb-12">
            
            {/* Brand Column */}
            <div className="md:col-span-4 lg:col-span-5 flex flex-col items-center md:items-start text-center md:text-left">
              <img src={LOGO} alt="WAGC Logo" className="h-16 mb-6 opacity-90" />
              <p className="text-white/60 text-sm leading-relaxed max-w-xs mb-6">
                Word Alive Global Center is a 501(c)(3) 5-fold Christian ministry engaging the region for advancement.
              </p>
              <div className="flex items-center gap-4">
                <a href="https://www.facebook.com/WordAliveGlobalCenter" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:bg-gold/20 hover:text-gold transition-colors duration-300">
                  <Facebook className="h-4 w-4" />
                </a>
                <a href="https://www.instagram.com/wordaliveglobalcenter" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:bg-gold/20 hover:text-gold transition-colors duration-300">
                  <Instagram className="h-4 w-4" />
                </a>
                <a href="https://www.youtube.com/@WordAliveGlobalCenter" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:bg-gold/20 hover:text-gold transition-colors duration-300">
                  <Youtube className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className="md:col-span-4 lg:col-span-3 flex flex-col items-center md:items-start">
              <h3 className="font-heading text-lg text-gold mb-5">Quick Links</h3>
              <div className="flex flex-col gap-3 text-sm text-white/70 text-center md:text-left">
                <Link to="/live-events" className="hover:text-gold transition-colors">Events & Calendar</Link>
                <Link to="/collectives" className="hover:text-gold transition-colors">Find a Collective</Link>
                <Link to="/bookstore" className="hover:text-gold transition-colors">Ministry Bookstore</Link>
                <Link to="/giving" className="hover:text-gold transition-colors">Give Online</Link>
                <Link to="/about" className="hover:text-gold transition-colors">About Us</Link>
              </div>
            </div>

            {/* Apostolic Covering */}
            <div className="md:col-span-4 lg:col-span-4 flex flex-col items-center md:items-start text-center md:text-left">
              <div className="w-12 h-12 rounded-full bg-navy border border-gold/30 flex items-center justify-center mb-4 shadow-[0_0_15px_rgba(212,175,55,0.15)]">
                <Users className="h-5 w-5 text-gold" />
              </div>
              <h3 className="font-heading text-lg text-bone mb-3">Apostolic Covering</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Under the Unbreakable Apostolic Headship of Apostle Dayna Milam.
              </p>
            </div>
          </div>

          <Separator className="bg-white/10 mb-6" />

          {/* Copyright & Legal */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs font-semibold uppercase tracking-widest text-white/40">
            <p>&copy; {new Date().getFullYear()} Word Alive Global Center.</p>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <span>|</span>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>
    </section>
  );
}